import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  FileText, Sheet, FileType, File as FileIcon, Folder as FolderIcon,
  Search, X, Check, Loader2, ChevronRight, ChevronLeft,
  type LucideIcon,
} from 'lucide-react'

import { drive, type DriveHit, type DriveFolder } from '../api'

const KIND_ICON: Record<DriveHit['kind'], LucideIcon> = {
  doc: FileText, sheet: Sheet, pdf: FileType, word: FileText, file: FileIcon,
}

// Top of the browse tree = the user's My Drive. 'root' is Google's alias for it.
const ROOT = { id: 'root', name: 'My Drive' }

/**
 * "Add material from Drive" — the attorney works their OWN connected Google
 * Drive and attaches docs to THIS matter (matter is implicit from where they
 * opened it). No pipeline; the pick is the authorization. Stays open after an
 * attach so several can be pulled in one go; live-refreshes pane-2 behind it.
 *
 * Two modes in one panel (Ken 2026-09-02):
 *   • empty search  → BROWSE the folder tree (breadcrumb + click into folders),
 *                     so users navigate the institutional terrain they know;
 *   • ≥2 chars typed → SEARCH the whole Drive (flat results), as before.
 * Selection + attach span both modes (keyed by file_id).
 */
export default function DriveMaterialModal({
  matter, open, onClose,
}: {
  matter: { id: number; code: string; name: string }
  open: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [results, setResults] = useState<DriveHit[]>([])
  const [connected, setConnected] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [attaching, setAttaching] = useState(false)
  const [added, setAdded] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  // Browse (folder-tree) state. `path` is the breadcrumb from My Drive down to
  // the folder currently open; its last entry is the folder we're listing.
  const [path, setPath] = useState<{ id: string; name: string }[]>([ROOT])
  const [browseFolders, setBrowseFolders] = useState<DriveFolder[]>([])
  const [browseFiles, setBrowseFiles] = useState<DriveHit[]>([])
  const [browseLoading, setBrowseLoading] = useState(false)

  const searching = debounced.length >= 2
  const currentFolder = path[path.length - 1]
  const currentFolderId = currentFolder.id

  // Reset + focus on open.
  useEffect(() => {
    if (!open) return
    setQ(''); setDebounced(''); setResults([]); setSelected(new Set()); setAdded([]); setError('')
    setPath([ROOT]); setBrowseFolders([]); setBrowseFiles([])
    const t = setTimeout(() => inputRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [open])

  // Debounce the query.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 350)
    return () => clearTimeout(t)
  }, [q])

  // SEARCH mode — fetch flat results when ≥2 chars typed.
  useEffect(() => {
    if (!open || !searching) return
    let cancelled = false
    setLoading(true); setError('')
    drive.search(debounced, matter.id)
      .then((res) => {
        if (cancelled) return
        setConnected(res.connected)
        setResults(res.results || [])
        if (res.error) setError(res.error)
      })
      .catch(() => { if (!cancelled) setError('Search failed.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [debounced, searching, open, matter.id])

  // BROWSE mode — list the current folder when not searching (and on navigation).
  useEffect(() => {
    if (!open || searching) return
    let cancelled = false
    setBrowseLoading(true); setError('')
    drive.browse(currentFolderId, matter.id)
      .then((res) => {
        if (cancelled) return
        setConnected(res.connected)
        setBrowseFolders(res.folders || [])
        setBrowseFiles(res.files || [])
        if (res.error) setError(res.error)
      })
      .catch(() => { if (!cancelled) setError('Could not open folder.') })
      .finally(() => { if (!cancelled) setBrowseLoading(false) })
    return () => { cancelled = true }
  }, [open, searching, currentFolderId, matter.id])

  const openFolder = (f: DriveFolder) => setPath((p) => [...p, { id: f.id, name: f.name }])
  const jumpTo = (idx: number) => setPath((p) => p.slice(0, idx + 1))

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const doAttach = async () => {
    const ids = Array.from(selected)
    if (!ids.length) return
    setAttaching(true)
    try {
      await drive.attach(matter.id, ids)
      const pool = [...results, ...browseFiles]
      const names = Array.from(new Set(
        pool.filter((r) => ids.includes(r.file_id)).map((r) => r.name)
      ))
      setAdded((prev) => [...prev, ...names])
      setSelected(new Set())
      const mark = (list: DriveHit[]) =>
        list.map((r) => (ids.includes(r.file_id) ? { ...r, already_attached: true } : r))
      setResults(mark); setBrowseFiles(mark)
      qc.invalidateQueries({ queryKey: ['artifacts', 'matter', matter.id] })
    } catch {
      setError('Attach failed.')
    } finally {
      setAttaching(false)
    }
  }

  const fileRow = (r: DriveHit) => {
    const Icon = KIND_ICON[r.kind] || FileIcon
    const checked = selected.has(r.file_id)
    return (
      <li key={r.file_id}>
        <button
          disabled={r.already_attached}
          onClick={() => toggle(r.file_id)}
          className={`w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left ${r.already_attached ? 'opacity-50 cursor-default' : 'hover:bg-[color:var(--me-hover)]'}`}
        >
          <span className={`flex h-4 w-4 items-center justify-center rounded-sm border me-border shrink-0 ${checked ? 'bg-[color:var(--me-selected)]' : ''}`}>
            {checked && <Check size={11} />}
          </span>
          <Icon size={14} className="me-text-mute shrink-0" />
          <span className="flex-1 min-w-0">
            <span className="block truncate text-[12px] me-text">{r.name}</span>
            <span className="block truncate text-[10.5px] me-text-mute">
              {r.modified_time ? new Date(r.modified_time).toLocaleDateString() : ''}{r.editor ? ` · ${r.editor}` : ''}
            </span>
          </span>
          {r.already_attached && <span className="me-chip text-[9px] shrink-0">In this matter</span>}
        </button>
      </li>
    )
  }

  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-[8vh]"
      onClick={onClose}
    >
      <div
        className="me-card w-[560px] max-w-[92vw] max-h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — names the destination matter so it's clear where docs land. */}
        <div className="flex items-center justify-between border-b me-border px-4 py-3">
          <div className="text-[13px] me-text">
            Add Drive material → <span className="me-mono">{matter.code}</span> · {matter.name}
          </div>
          <button onClick={onClose} className="me-text-mute hover:me-text" aria-label="Close"><X size={15} /></button>
        </div>

        {/* Search */}
        <div className="px-4 pt-3">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 me-text-mute pointer-events-none" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search all of your Drive, or browse folders below…"
              className="me-input !w-full !pl-9"
            />
          </div>
        </div>

        {/* Breadcrumb — only in browse mode. Each crumb jumps back to that level. */}
        {!searching && connected && (
          <div className="flex items-center flex-wrap gap-0.5 px-4 pt-2 text-[11px]">
            {path.length > 1 && (
              <button
                onClick={() => jumpTo(path.length - 2)}
                className="me-text-mute hover:me-text mr-0.5 inline-flex items-center"
                aria-label="Up one folder"
              ><ChevronLeft size={13} /></button>
            )}
            {path.map((c, i) => (
              <span key={`${c.id}-${i}`} className="inline-flex items-center">
                {i > 0 && <ChevronRight size={11} className="me-text-mute mx-0.5 shrink-0" />}
                <button
                  onClick={() => jumpTo(i)}
                  disabled={i === path.length - 1}
                  className={i === path.length - 1 ? 'me-text font-medium' : 'me-text-mute hover:me-text'}
                >{c.name}</button>
              </span>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-auto px-2 py-2">
          {!connected ? (
            <div className="p-4 text-[12px] me-text-mute">Your Google Drive isn't connected.</div>
          ) : searching ? (
            /* SEARCH results */
            loading ? (
              <div className="p-4 text-[12px] me-text-mute flex items-center gap-2">
                <Loader2 size={13} className="animate-spin" /> Searching…
              </div>
            ) : results.length === 0 ? (
              <div className="p-4 text-[12px] me-text-mute">No Drive files match &ldquo;{debounced}&rdquo;.</div>
            ) : (
              <ul>{results.map(fileRow)}</ul>
            )
          ) : (
            /* BROWSE the current folder */
            browseLoading ? (
              <div className="p-4 text-[12px] me-text-mute flex items-center gap-2">
                <Loader2 size={13} className="animate-spin" /> Opening folder…
              </div>
            ) : (browseFolders.length === 0 && browseFiles.length === 0) ? (
              <div className="p-4 text-[12px] me-text-mute">This folder has no sub-folders or attachable documents.</div>
            ) : (
              <ul>
                {browseFolders.map((f) => (
                  <li key={`fold-${f.id}`}>
                    <button
                      onClick={() => openFolder(f)}
                      className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-[color:var(--me-hover)]"
                    >
                      <span className="h-4 w-4 shrink-0" />
                      <FolderIcon size={14} className="me-text-mute shrink-0" />
                      <span className="flex-1 min-w-0 truncate text-[12px] me-text">{f.name}</span>
                      <ChevronRight size={13} className="me-text-mute shrink-0" />
                    </button>
                  </li>
                ))}
                {browseFiles.map(fileRow)}
              </ul>
            )
          )}
          {error && <div className="px-2 py-1 text-[11px] text-[color:var(--me-urgent)]">{error}</div>}
        </div>

        {/* Footer */}
        <div className="border-t me-border px-4 py-2.5 flex items-center gap-3">
          <button
            onClick={doAttach}
            disabled={selected.size === 0 || attaching}
            className="me-btn me-btn-primary"
          >
            {attaching ? 'Attaching…' : selected.size ? `Attach ${selected.size}` : 'Attach'}
          </button>
          {added.length > 0 && (
            <span className="text-[11px] me-text-mute truncate">✓ Added: {added.join(', ')}</span>
          )}
          <button onClick={onClose} className="me-btn me-btn-outline ml-auto">Close</button>
        </div>
      </div>
    </div>
  )
}
